import { HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { $app } from '../../../app.service'
import { LumpSumBeneficariesWorkpackagesDataResponse } from '../../../features/feature-lump-sum/models/lump-sum-beneficiaries-workpackages-data.interface'
import { LumpSumChangeHistoryBody } from '../../../features/feature-lump-sum/models/lump-sum-change-history.interface'
import { LumpSumCommentsBody } from '../../../features/feature-lump-sum/models/lump-sum-comments.interface'
import { LumpSumDepreciationBody } from '../../../features/feature-lump-sum/models/lump-sum-depreciation.interface'
import { LumpSumWpTableData } from '../../../features/feature-lump-sum/models/lump-sum-wp-table-data.interface'
import { AppBasicBeneficiary } from '../../models/interfaces/app-basic-beneficiary.interface'
import { AppBasicResourceTypesResponse } from '../../models/interfaces/app-basic-resource-types.interface'
import { AppBasicWorkpackage } from '../../models/interfaces/app-basic-workpackage.interface'
import { AppBasicService } from '../../services/app-basic.service'

@Injectable()
export class LumpSumService extends AppBasicService {
   get_LS_WorkpackageList(): Observable<AppBasicWorkpackage[]> {
      if (this.console) console.log(this.logPrefix, 'get_LS_WorkpackageList')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<AppBasicWorkpackage[]>(`${this._configService.apiUrl}/workplans/projects/${projectId}/workpackages`).pipe(
         /**
          * we used the same service used in the WBS section for the workpackages only that in this case we only need
          * the three fields so for cleanliness and to not replicate the API on the BE side we manage it like this
          */
         map((response) =>
            (response ?? []).map((wp) => ({
               id: wp.id,
               taskId: wp.taskId,
               title: wp.title
            }))
         )
      )
   }

   get_LS_SummaryForWpData(workPackageId: number): Observable<LumpSumWpTableData> {
      if (this.console) console.log(this.logPrefix, 'get_LS_SummaryForWpData', workPackageId)

      const projectId = $app.getCurrentProjectId()

      /**
       * id=0 means tab sum of all workpackages
       *  */
      let path = workPackageId === 0 ? `/costs/total/summary` : `/workpackages/${workPackageId}/costs/summary`

      return this.http.get<LumpSumWpTableData>(`${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum${path}`)
   }

   get_LS_BeneficiariesList(): Observable<AppBasicBeneficiary[]> {
      if (this.console) console.log(this.logPrefix, 'get_LS_BeneficiariesList')
      const projectId = $app.getCurrentProjectId()

      return this.http.get<AppBasicBeneficiary[]>(`${this._configService.apiUrl}/budgets/projects/${projectId}/partners/visibility`).pipe(
         map((response) =>
            (response ?? []).map((be) => ({
               id: be.id,
               shortName: be.shortName
            }))
         )
      )
   }

   get_LS_SummaryForBeneficiaryData(beneficiaryId: number): Observable<LumpSumWpTableData> {
      if (this.console) console.log(this.logPrefix, 'get_LS_SummaryForBeneficiaryData', beneficiaryId)

      const projectId = $app.getCurrentProjectId()

      return this.http.get<LumpSumWpTableData>(
         `${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/partners/${beneficiaryId}/costs/summary`
      )
   }

   get_LS_CostsPerWpSingleWpTableData(beneficiaryId: number, workPackageId: number): Observable<LumpSumWpTableData> {
      if (this.console) console.log(this.logPrefix, 'get_LS_CostsPerWpSingleWpTableData', beneficiaryId, workPackageId)

      const projectId = $app.getCurrentProjectId()

      return this.http.get<LumpSumWpTableData>(
         `${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/workpackages/${workPackageId}/partners/${beneficiaryId}/costs`
      )
   }

   update_LS_CostsPerWpSingleWpTableData(beneficiaryId: number, workPackageId: number, requestBody: LumpSumWpTableData): Observable<any> {
      if (this.console) console.log(this.logPrefix, 'update_LS_CostsPerWpSingleWpTableData', beneficiaryId, workPackageId, requestBody)

      const projectId = $app.getCurrentProjectId()
      return this.http.put(
         `${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/workpackages/${workPackageId}/partners/${beneficiaryId}/costs`,
         requestBody
      )
   }

   get_LS_EstimatedBreakdownData(): Observable<LumpSumBeneficariesWorkpackagesDataResponse> {
      if (this.console) console.log(this.logPrefix, 'get_LS_EstimatedBreakdownData')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<LumpSumBeneficariesWorkpackagesDataResponse>(
         `${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/costs/breakdown/summary`
      )
   }

   get_LS_PersonMonthsData(): Observable<LumpSumBeneficariesWorkpackagesDataResponse> {
      if (this.console) console.log(this.logPrefix, 'get_LS_PersonMonthsData')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<LumpSumBeneficariesWorkpackagesDataResponse>(
         `${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/costs/person-months/summary`
      )
   }

   get_LS_DepreciationData(beneficiaryId: number, workPackageId: number): Observable<LumpSumDepreciationBody> {
      if (this.console) console.log(this.logPrefix, 'get_LS_DepreciationData', beneficiaryId, workPackageId)

      const projectId = $app.getCurrentProjectId()
      return this.http.get<LumpSumDepreciationBody>(
         `${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/workpackages/${workPackageId}/partners/${beneficiaryId}/deprecate/costs`
      )
   }

   update_LS_DepreciationData(beneficiaryId: number, workPackageId: number, requestBody: LumpSumDepreciationBody): Observable<any> {
      if (this.console) console.log(this.logPrefix, 'update_LS_DepreciationData', beneficiaryId, workPackageId, requestBody)

      const projectId = $app.getCurrentProjectId()

      return this.http.put(
         `${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/workpackages/${workPackageId}/partners/${beneficiaryId}/deprecate/costs`,
         requestBody
      )
   }

   get_LS_ResourceTypes(): Observable<AppBasicResourceTypesResponse> {
      if (this.console) console.log(this.logPrefix, 'get_LS_ResourceTypes')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<AppBasicResourceTypesResponse>(`${this._configService.apiUrl}/budgets/projects/${projectId}/lump-sum/deprecation/resources-type`)
   }

   get_LS_AnyComments(beneficiaryId: number, workPackageId: number): Observable<LumpSumCommentsBody> {
      if (this.console) console.log(this.logPrefix, 'get_LS_AnyComments', beneficiaryId, workPackageId)

      const projectId = $app.getCurrentProjectId()
      return this.http.get<LumpSumCommentsBody>(
         `${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/workpackages/${workPackageId}/partners/${beneficiaryId}/any-comments`
      )
   }

   update_LS_AnyComments(beneficiaryId: number, workPackageId: number, requestBody: LumpSumCommentsBody): Observable<any> {
      if (this.console) console.log(this.logPrefix, 'update_LS_AnyComments', beneficiaryId, workPackageId, requestBody)

      const projectId = $app.getCurrentProjectId()
      return this.http.put(
         `${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/workpackages/${workPackageId}/partners/${beneficiaryId}/any-comments`,
         requestBody
      )
   }

   get_LS_ChangeHistory(): Observable<LumpSumChangeHistoryBody> {
      if (this.console) console.log(this.logPrefix, 'get_LS_ChangeHistory')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<LumpSumChangeHistoryBody>(`${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/changeHistories`)
   }

   update_LS_ChangeHistory(requestBody: LumpSumChangeHistoryBody): Observable<any> {
      if (this.console) console.log(this.logPrefix, 'update_LS_ChangeHistory', requestBody)

      const projectId = $app.getCurrentProjectId()

      return this.http.put(`${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/changeHistories`, requestBody)
   }

   exportLumpSum(): Observable<any> {
      if (this.console) console.log(this.logPrefix, 'exportLumpSum')

      const projectId = $app.getCurrentProjectId()

      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json'
         }),
         reportProgress: true,
         observe: 'events' as 'body',
         responseType: 'blob' as 'json'
      }

      return this.http.get(`${this._configService.newApiUrl}/budgets/projects/${projectId}/lump-sum/export-budget`, httpOptions)
   }
}
